'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import type { HTMLAttributes, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PrRef } from './session-panel/pr-ref';
import { describeChecks, describeCubicIssues } from './pr-link-hover-card-model';
import { parsePrLink, samePrRef } from './session-panel/pr-ref';
import { useSessionPrCardStatus } from './session-pr-card-status';
import { sessionPrStatePresentation } from './session-pr-state';
import { getInboxCheckStatusPresentation } from '@/components/inbox/pr-inbox-check-status-presentation';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DiffLineStats } from '@/components/ui/diff-line-stats';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchPrV2Summary } from '@/data/v2/pr/fetch-pr-v2';
import { PR_V2_SUMMARY_STALE_TIME_MS } from '@/data/v2/pr/query-config';
import { prV2SummaryQueryKey } from '@/data/v2/pr/query-keys';
import { cn } from '@/lib/utils';
import { getFromNow } from '@/lib/utils/date-formatters';

// Long enough that moving the pointer across a paragraph opens nothing, short enough to feel
// like a response to resting on the link.
const OPEN_DELAY_MS = 350;
// Time to cross the gap between the link and the card without it closing.
const CLOSE_DELAY_MS = 150;
// Moving from one PR link to another within this long of a card beginning to close opens the next at once.
const REOPEN_WINDOW_MS = 400;

// When any PR hover card last began closing, so the next one can skip its delay.
let lastHoverCardClosingAt = 0;

/**
 * Opens after the pointer rests, stays open while it moves into the card, and closes shortly after
 * it leaves both.
 */
function useHoverCardState() {
  const $ = _c(10);
  const [open, setOpenState] = useState(false);
  const isOpen = useRef(false);
  const timer = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const clearTimer = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      isOpen.current = next;
      setOpenState(next);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setOpen = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (next_0, delay) => {
      clearTimer();
      if (isOpen.current && !next_0) {
        lastHoverCardClosingAt = Date.now();
      }
      if (delay <= 0) {
        setOpen(next_0);
        return;
      }
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setOpen(next_0);
      }, delay);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const schedule = t2;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => clearTimer;
    t4 = [clearTimer];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = delay_0 => {
      const reopening = Date.now() - lastHoverCardClosingAt < REOPEN_WINDOW_MS;
      schedule(true, reopening ? 0 : delay_0);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => schedule(false, CLOSE_DELAY_MS);
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => schedule(false, 0);
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== open) {
    t8 = {
      open,
      show: t5,
      keepOpen: clearTimer,
      hide: t6,
      close: t7
    };
    $[8] = open;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}

/** Starts a one-shot summary read as soon as a link is hovered, so the card often opens with its title. */
function SummaryPrefetch(t0) {
  const $ = _c(6);
  const {
    prRef
  } = t0;
  const queryClient = useQueryClient();
  const {
    owner,
    repo,
    number
  } = prRef;
  let t1;
  let t2;
  if ($[0] !== number || $[1] !== owner || $[2] !== queryClient || $[3] !== repo) {
    t1 = () => {
      queryClient.prefetchQuery({
        queryKey: prV2SummaryQueryKey(owner, repo, String(number)),
        queryFn: t3 => {
          const {
            signal
          } = t3;
          return fetchPrV2Summary(owner, repo, String(number), signal);
        },
        staleTime: PR_V2_SUMMARY_STALE_TIME_MS
      });
    };
    t2 = [queryClient, owner, repo, number];
    $[0] = number;
    $[1] = owner;
    $[2] = queryClient;
    $[3] = repo;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return null;
}
function CardHeader(t0) {
  const $ = _c(10);
  const {
    prRef,
    state
  } = t0;
  const t1 = `${prRef.owner}/${prRef.repo}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="truncate">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `#${prRef.number}`;
  let t4;
  if ($[2] !== t3) {
    t4 = <span className="shrink-0 text-zinc-500">{t3}</span>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== state) {
    t5 = state ? <><span aria-hidden="true" className="text-zinc-600">·</span>{state}</> : null;
    $[4] = state;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-zinc-400">{t2}{t4}{t5}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

/**
 * The card's contents. It mounts when the card opens and reads the same PR v2 queries as the PR
 * tab, so opening the pull request after hovering it starts on a warm cache.
 */
function PrHoverCardBody(t0) {
  const $ = _c(71);
  const {
    prRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      deferCubicReview: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const status = useSessionPrCardStatus(prRef, t1);
  const pullRequest = status.pullRequest;
  if (!pullRequest && status.loadFailure) {
    let t2;
    if ($[1] !== prRef) {
      t2 = <CardHeader prRef={prRef} state={null} />;
      $[1] = prRef;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = status.loadFailure === "no-access" ? "You don't have access to this pull request in cubic." : "This pull request could not be loaded.";
    let t4;
    if ($[3] !== t3) {
      t4 = <p className="mt-1 text-zinc-400">{t3}</p>;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] !== t2 || $[6] !== t4) {
      t5 = <>{t2}{t4}</>;
      $[5] = t2;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  if (!pullRequest) {
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton className="h-2.5 w-10" />;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== prRef) {
      t3 = <CardHeader prRef={prRef} state={t2} />;
      $[9] = prRef;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Skeleton className="mt-2 h-3 w-11/12" />;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="mt-2 flex items-center gap-1.5"><Skeleton className="size-3.5 rounded-full" /><Skeleton className="h-2.5 w-2/5" /></div>;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t3) {
      t6 = <div aria-busy="true">{t3}{t4}{t5}</div>;
      $[13] = t3;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  let T0;
  let T1;
  let authorLogin;
  let checks;
  let diff;
  let issues;
  let openedAt;
  let presentation;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[15] !== prRef || $[16] !== pullRequest || $[17] !== status.checks || $[18] !== status.cubicReview || $[19] !== status.diff) {
    presentation = sessionPrStatePresentation(pullRequest);
    authorLogin = pullRequest.author.login;
    openedAt = pullRequest.openedAt;
    let t6;
    if ($[32] !== status.cubicReview) {
      t6 = describeCubicIssues(status.cubicReview);
      $[32] = status.cubicReview;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    issues = t6;
    let t7;
    if ($[34] !== status.checks) {
      t7 = status.checks ? {
        Icon: getInboxCheckStatusPresentation(status.checks).Icon,
        text: describeChecks(status.checks)
      } : null;
      $[34] = status.checks;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    checks = t7;
    diff = status.diff && (status.diff.additions > 0 || status.diff.deletions > 0) ? status.diff : null;
    T1 = CardHeader;
    t5 = prRef;
    t4 = "inline-flex shrink-0 items-center gap-1 text-zinc-300";
    T0 = presentation.icon;
    t2 = "true";
    t3 = cn("size-3", presentation.className);
    $[15] = prRef;
    $[16] = pullRequest;
    $[17] = status.checks;
    $[18] = status.cubicReview;
    $[19] = status.diff;
    $[20] = T0;
    $[21] = T1;
    $[22] = authorLogin;
    $[23] = checks;
    $[24] = diff;
    $[25] = issues;
    $[26] = openedAt;
    $[27] = presentation;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
  } else {
    T0 = $[20];
    T1 = $[21];
    authorLogin = $[22];
    checks = $[23];
    diff = $[24];
    issues = $[25];
    openedAt = $[26];
    presentation = $[27];
    t2 = $[28];
    t3 = $[29];
    t4 = $[30];
    t5 = $[31];
  }
  let t6;
  if ($[36] !== T0 || $[37] !== t2 || $[38] !== t3) {
    t6 = <T0 aria-hidden={t2} className={t3} />;
    $[36] = T0;
    $[37] = t2;
    $[38] = t3;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  let t7;
  if ($[40] !== presentation.shortLabel || $[41] !== t4 || $[42] !== t6) {
    t7 = <span className={t4}>{t6}{presentation.shortLabel}</span>;
    $[40] = presentation.shortLabel;
    $[41] = t4;
    $[42] = t6;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  let t8;
  if ($[44] !== T1 || $[45] !== t5 || $[46] !== t7) {
    t8 = <T1 prRef={t5} state={t7} />;
    $[44] = T1;
    $[45] = t5;
    $[46] = t7;
    $[47] = t8;
  } else {
    t8 = $[47];
  }
  const t9 = pullRequest.title ?? `Pull request #${prRef.number}`;
  let t10;
  if ($[48] !== t9) {
    t10 = <p className="mt-1 line-clamp-2 text-[13px] leading-snug font-semibold text-zinc-50">{t9}</p>;
    $[48] = t9;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  let t11;
  if ($[50] !== authorLogin || $[51] !== pullRequest.author.avatarUrl) {
    t11 = authorLogin ? <><Avatar className="size-3.5 shrink-0"><AvatarImage src={pullRequest.author.avatarUrl ?? undefined} alt="" /><AvatarFallback className="text-[7px]">{authorLogin.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar><span className="truncate">{authorLogin}</span></> : null;
    $[50] = authorLogin;
    $[51] = pullRequest.author.avatarUrl;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== authorLogin || $[54] !== openedAt) {
    t12 = authorLogin && openedAt ? <span aria-hidden="true" className="text-zinc-600">·</span> : null;
    $[53] = authorLogin;
    $[54] = openedAt;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  let t13;
  if ($[56] !== openedAt) {
    t13 = openedAt ? <span className="shrink-0">{`opened ${getFromNow(openedAt)}`}</span> : null;
    $[56] = openedAt;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== t11 || $[59] !== t12 || $[60] !== t13) {
    t14 = <div className="mt-1 flex min-w-0 items-center gap-1.5 text-[11px] text-zinc-400">{t11}{t12}{t13}</div>;
    $[58] = t11;
    $[59] = t12;
    $[60] = t13;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] !== checks || $[63] !== diff || $[64] !== issues) {
    t15 = issues || checks || diff ? <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-zinc-800 pt-2 text-[11px] whitespace-nowrap text-zinc-400">{issues ? <span className="inline-flex items-center gap-1"><span aria-hidden="true" className="size-1.5 rounded-full bg-zinc-500" />{issues}</span> : null}{checks ? <span className="inline-flex items-center gap-1"><checks.Icon aria-hidden="true" className="size-3" strokeWidth={2.4} />{checks.text}</span> : null}{diff ? <DiffLineStats additions={diff.additions} deletions={diff.deletions} compact={true} /> : null}</div> : null;
    $[62] = checks;
    $[63] = diff;
    $[64] = issues;
    $[65] = t15;
  } else {
    t15 = $[65];
  }
  let t16;
  if ($[66] !== t10 || $[67] !== t14 || $[68] !== t15 || $[69] !== t8) {
    t16 = <>{t8}{t10}{t14}{t15}</>;
    $[66] = t10;
    $[67] = t14;
    $[68] = t15;
    $[69] = t8;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  return t16;
}
type HoveredLink = {
  anchor: HTMLAnchorElement;
  prRef: PrRef;
};

/**
 * Previews the pull requests linked inside it: resting the pointer on a pull request link opens a
 * card with its repository, state, title and author, then one line with cubic's open issues, the
 * checks and the size. Clicking the link, opening its menu or pressing the card closes it.
 *
 * It listens for hovers on the whole region rather than on each link, so it also previews links in
 * HTML it did not render, such as rendered markdown. Focus does not open the card: a link's menu
 * hands focus back to the link as it closes, which would reopen the card the menu just closed.
 */
export function PrLinkHoverCards(t0) {
  const $ = _c(36);
  const {
    children,
    linkSelector: t1,
    exclude
  } = t0;
  const linkSelector = t1 === undefined ? "a[href]" : t1;
  const {
    open,
    show,
    keepOpen,
    hide,
    close
  } = useHoverCardState();
  const [hovered, setHovered] = useState(null);
  let t2;
  if ($[0] !== exclude || $[1] !== linkSelector) {
    t2 = target => {
      const anchor = target instanceof Element ? target.closest(linkSelector) : null;
      if (!(anchor instanceof HTMLAnchorElement)) {
        return null;
      }
      const prRef = parsePrLink(anchor.href);
      return prRef && !(exclude && samePrRef(prRef, exclude)) ? {
        anchor,
        prRef
      } : null;
    };
    $[0] = exclude;
    $[1] = linkSelector;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const pullRequestLinkAt = t2;
  let t3;
  if ($[3] !== hovered?.anchor || $[4] !== pullRequestLinkAt || $[5] !== show) {
    t3 = event => {
      if (event.pointerType !== "mouse" && event.pointerType !== "pen") {
        return;
      }
      const link = pullRequestLinkAt(event.target);
      if (!link || isInside(link.anchor, event.relatedTarget)) {
        return;
      }
      if (link.anchor !== hovered?.anchor) {
        setHovered(link);
      }
      show(OPEN_DELAY_MS);
    };
    $[3] = hovered?.anchor;
    $[4] = pullRequestLinkAt;
    $[5] = show;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== hide || $[8] !== hovered?.anchor) {
    t4 = event_0 => {
      const anchor_0 = hovered?.anchor;
      if (anchor_0 && isInside(anchor_0, event_0.target) && !isInside(anchor_0, event_0.relatedTarget)) {
        hide();
      }
    };
    $[7] = hide;
    $[8] = hovered?.anchor;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== close || $[11] !== pullRequestLinkAt) {
    t5 = event_1 => {
      if (pullRequestLinkAt(event_1.target)) {
        close();
      }
    };
    $[10] = close;
    $[11] = pullRequestLinkAt;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== close || $[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t6 = {
      className: "contents",
      onPointerOver: t3,
      onPointerOut: t4,
      onClick: t5,
      onContextMenu: close
    };
    $[13] = close;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const regionProps = t6;
  let t7;
  if ($[18] !== close) {
    t7 = next => {
      if (!next) {
        close();
      }
    };
    $[18] = close;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== hovered) {
    t8 = hovered ? <PopoverAnchor virtualRef={{
      current: hovered.anchor
    }} /> : null;
    $[20] = hovered;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== children || $[23] !== regionProps) {
    t9 = <div {...regionProps}>{children}</div>;
    $[22] = children;
    $[23] = regionProps;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== close || $[26] !== hide || $[27] !== hovered || $[28] !== keepOpen) {
    t10 = hovered ? <><SummaryPrefetch prRef={hovered.prRef} /><PopoverContent side="bottom" align="start" sideOffset={6} collisionPadding={12} hideWhenDetached={true} className="w-75 rounded-[10px] border-zinc-700 bg-zinc-900 px-3 py-2.5 text-xs text-zinc-100 shadow-xl" onOpenAutoFocus={_temp} onCloseAutoFocus={_temp2} onPointerEnter={keepOpen} onPointerLeave={hide} onPointerDown={close} data-testid="pr-link-hover-card"><PrHoverCardBody prRef={hovered.prRef} /></PopoverContent></> : null;
    $[25] = close;
    $[26] = hide;
    $[27] = hovered;
    $[28] = keepOpen;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== open || $[31] !== t10 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t11 = <Popover open={open} onOpenChange={t7}>{t8}{t9}{t10}</Popover>;
    $[30] = open;
    $[31] = t10;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp2(event_3) {
  return event_3.preventDefault();
}
function _temp(event_2) {
  return event_2.preventDefault();
}
function isInside(element: Element, target: EventTarget | null): boolean {
  return target instanceof Node && element.contains(target);
}